import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { updateVideoProgress, recordWatchSession } from '../services/courseService';
import Plyr from 'plyr';
import 'plyr/dist/plyr.css';

// إضافة تعريفات YouTube IFrame API
declare global {
  interface Window {
    YT: {
      Player: any;
      PlayerState: {
        PLAYING: number;
        PAUSED: number;
        ENDED: number;
      };
    };
    onYouTubeIframeAPIReady: () => void;
  }
}

interface VideoPlayerProps {
  videoUrl: string;
  courseId: string;
  lessonId: string;
  userId: string;
  initialProgress?: number;
  onComplete?: () => void;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ 
  videoUrl, 
  courseId, 
  lessonId, 
  userId,
  initialProgress = 0,
  onComplete 
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<Plyr | null>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentProgress, setCurrentProgress] = useState<number>(initialProgress);
  const progressUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isYoutubeVideo, setIsYoutubeVideo] = useState<boolean>(false);
  const [youtubeVideoId, setYoutubeVideoId] = useState<string | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // إضافة متغيرات لتتبع وقت المشاهدة بدقة
  const watchSessionRef = useRef<{
    startTime: number;
    totalWatchedTime: number;
    lastUpdateTime: number;
    isPlaying: boolean;
    watchCount: number;
    videoLength: number | null;
  }>({
    startTime: 0,
    totalWatchedTime: 0,
    lastUpdateTime: 0,
    isPlaying: false,
    watchCount: 0,
    videoLength: null
  });

  // تعريف دالة لحفظ جلسة المشاهدة
  const saveWatchSession = useCallback(async () => {
    if (!playerRef.current || !watchSessionRef.current.videoLength) return;
    
    const { totalWatchedTime, videoLength, watchCount } = watchSessionRef.current;
    const currentPosition = playerRef.current.currentTime || 0;
    const completionPercentage = Math.min(Math.round((totalWatchedTime / videoLength) * 100), 100);
    const isCompleted = completionPercentage >= 90;
    
    try {
      await recordWatchSession(courseId, lessonId, userId, {
        watchedSeconds: totalWatchedTime,
        totalSeconds: videoLength,
        completionPercentage,
        lastPosition: currentPosition,
        isCompleted,
        watchCount
      });
      
      // تحديث التقدم المحلي
      setCurrentProgress(completionPercentage);
      
      // إذا كان الفيديو مكتملًا، قم بتشغيل الدالة onComplete
      if (isCompleted && onComplete && completionPercentage > initialProgress) {
        onComplete();
      }
    } catch (err) {
      console.error('خطأ في حفظ جلسة المشاهدة:', err);
    }
  }, [courseId, lessonId, userId, onComplete, initialProgress]);

  // التعرف على نوع الفيديو (YouTube أو محلي)
  useEffect(() => {
    // تحسين التعبير النمطي للتعرف على روابط YouTube بمختلف الصيغ
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([^"&?\/\s]{11})/i;
    
    // استخراج معرف الفيديو من الرابط
    let videoId = null;
    const match = videoUrl.match(youtubeRegex);
    
    if (match && match[1]) {
      videoId = match[1];
    } else if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      // محاولة استخراج معرف الفيديو من URL parameters
      try {
        const url = new URL(videoUrl);
        const params = new URLSearchParams(url.search);
        videoId = params.get('v');
      } catch (e) {
        console.error('خطأ في تحليل رابط YouTube:', e);
      }
    }
    
    if (videoId) {
      setIsYoutubeVideo(true);
      setYoutubeVideoId(videoId);
    } else {
      setIsYoutubeVideo(false);
      setYoutubeVideoId(null);
    }
  }, [videoUrl]);

  // تهيئة مشغل Plyr
  useEffect(() => {
    if (!playerContainerRef.current) return;
    
    // تنظيف أي مشغل سابق
    if (playerRef.current) {
      playerRef.current.destroy();
    }
    
    // إنشاء عنصر الفيديو المناسب
    let mediaElement: HTMLElement;
    
    if (isYoutubeVideo && youtubeVideoId) {
      // إنشاء عنصر div لتضمين فيديو YouTube
      const div = document.createElement('div');
      div.setAttribute('data-plyr-provider', 'youtube');
      div.setAttribute('data-plyr-embed-id', youtubeVideoId);
      playerContainerRef.current.innerHTML = '';
      playerContainerRef.current.appendChild(div);
      mediaElement = div;
    } else {
      // إنشاء عنصر video لتشغيل الفيديو المحلي
      const video = document.createElement('video');
      video.controls = true;
      video.crossOrigin = 'anonymous';
      
      const source = document.createElement('source');
      source.src = videoUrl;
      source.type = 'video/mp4';
      
      video.appendChild(source);
      playerContainerRef.current.innerHTML = '';
      playerContainerRef.current.appendChild(video);
      mediaElement = video;
    }
    
    // تهيئة مشغل Plyr مع إعدادات مخصصة
    const player = new Plyr(mediaElement, {
      controls: [
        'play-large', 'play', 'progress', 'current-time', 'mute', 
        'volume', 'settings', 'fullscreen'
      ],
      settings: ['captions', 'quality', 'speed'],
      hideControls: false,
      resetOnEnd: false,
      keyboard: { focused: true, global: false },
      tooltips: { controls: true, seek: true },
      youtube: {
        noCookie: true,
        rel: 0,
        showinfo: 0,
        iv_load_policy: 3,
        modestbranding: 1,
        playsinline: 1,
        disablekb: 1,
        // إخفاء عناصر التحكم الأصلية في YouTube
        controls: 0
      }
    });
    
    playerRef.current = player;
    
    // معالجة أحداث المشغل
    player.on('ready', () => {
      setLoading(false);
      
      // ضبط موضع التشغيل حسب التقدم المحفوظ
      if (player.duration) {
        watchSessionRef.current.videoLength = player.duration;
      }
      
      if (initialProgress > 0 && player.duration) {
        const seekToSeconds = (initialProgress / 100) * player.duration;
        player.currentTime = seekToSeconds;
      }
    });
    
    player.on('loadedmetadata', () => {
      // ضبط طول الفيديو بعد تحميل البيانات
      if (player.duration) {
        watchSessionRef.current.videoLength = player.duration;
      }
    });
    
    player.on('play', () => {
      // بدء تتبع التقدم عند التشغيل
      const now = Date.now();
      watchSessionRef.current.startTime = now;
      watchSessionRef.current.lastUpdateTime = now;
      watchSessionRef.current.isPlaying = true;
      
      // زيادة عدد مرات المشاهدة عند بدء الفيديو
      if (player.currentTime < 1) {
        watchSessionRef.current.watchCount += 1;
      }
    });
    
    player.on('playing', () => {
      // بدء تتبع التقدم عند التشغيل
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      
      progressIntervalRef.current = setInterval(() => {
        if (player && player.currentTime && player.duration) {
          // تحديث وقت المشاهدة
          const now = Date.now();
          const timeSinceLastUpdate = (now - watchSessionRef.current.lastUpdateTime) / 1000;
          
          // تحديث وقت المشاهدة إذا كان الفارق أقل من 5 ثواني
          if (timeSinceLastUpdate > 0 && timeSinceLastUpdate < 5) {
            watchSessionRef.current.totalWatchedTime += timeSinceLastUpdate;
          }
          
          watchSessionRef.current.lastUpdateTime = now;
          
          // تحديث التقدم
          const progress = Math.round((player.currentTime / player.duration) * 100);
          setCurrentProgress(progress);
          
          // تأخير تحديثات التقدم لتجنب الكتابة المتكررة في Firestore
          if (progressUpdateTimeoutRef.current) {
            clearTimeout(progressUpdateTimeoutRef.current);
          }
          
          progressUpdateTimeoutRef.current = setTimeout(() => {
            saveWatchSession();
          }, 5000); // تحديث كل 5 ثواني بدلاً من 2 ثانية
        }
      }, 1000);
    });
    
    player.on('pause', () => {
      // إيقاف تتبع التقدم عند الإيقاف المؤقت
      watchSessionRef.current.isPlaying = false;
      
      // حفظ جلسة المشاهدة عند الإيقاف المؤقت
      saveWatchSession();
      
      // إيقاف تتبع التقدم
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    });
    
    player.on('ended', () => {
      // إيقاف تتبع التقدم عند انتهاء الفيديو
      watchSessionRef.current.isPlaying = false;
      
      // تحديث التقدم إلى 100%
      setCurrentProgress(100);
      
      // حفظ جلسة المشاهدة عند انتهاء الفيديو
      if (watchSessionRef.current.videoLength) {
        watchSessionRef.current.totalWatchedTime = watchSessionRef.current.videoLength;
        saveWatchSession();
      }
      
      // إيقاف تتبع التقدم
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    });
    
    player.on('error', () => {
      setError('حدث خطأ أثناء تحميل الفيديو. يرجى المحاولة مرة أخرى.');
      setLoading(false);
    });
    
    // تنظيف الموارد عند إلغاء تحميل المكون
    return () => {
      // حفظ جلسة المشاهدة قبل إلغاء تحميل المكون
      if (watchSessionRef.current.isPlaying && watchSessionRef.current.totalWatchedTime > 0) {
        saveWatchSession();
      }
      
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (progressUpdateTimeoutRef.current) {
        clearTimeout(progressUpdateTimeoutRef.current);
      }
      if (playerRef.current) {
        playerRef.current.destroy();
      }
    };
  }, [isYoutubeVideo, youtubeVideoId, videoUrl, courseId, lessonId, userId, initialProgress, onComplete, saveWatchSession]);

  return (
    <div className="video-player-container">
      {loading && (
        <div className="text-center p-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">جاري تحميل الفيديو...</p>
        </div>
      )}
      
      {error && (
        <Alert variant="danger">{error}</Alert>
      )}
      
      <div 
        ref={playerContainerRef} 
        className="plyr-container w-100"
        style={{ display: loading ? 'none' : 'block' }}
      ></div>
      
      <div className="mt-3">
        <ProgressBar 
          now={currentProgress} 
          label={`${currentProgress}%`}
          variant={currentProgress >= 90 ? 'success' : 'primary'}
        />
      </div>
      
      {currentProgress >= 90 && (
        <div className="mt-2 text-success">
          <small>✓ لقد أكملت مشاهدة هذا الدرس</small>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
